import { module as BaseModule } from 'modujs'
import { createRenderer } from './background/createRenderer'
import { createPanel } from './background/panel'
import {
  BLUSH_PARAMS,
  EXPORT_HEIGHT,
  EXPORT_WIDTH,
  toSerializable,
} from './background/params'
import { fragSrc, vertSrc } from './background/shaders'

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Persist Background across Highway re-inits (element stays outside router-view). */
const instances = new WeakMap()

export default class extends BaseModule {
  init() {
    const existing = instances.get(this.el)
    if (existing?.renderer) {
      this.params = existing.params
      this.phase = existing.phase
      this.playing = existing.playing
      this.panelHidden = existing.panelHidden
      this.panel = existing.panel
      this.renderer = existing.renderer
      this.raf = existing.raf
      this.lastTime = existing.lastTime
      this.hint = existing.hint
      this.canvas = existing.canvas
      this.motionQuery = existing.motionQuery
      this.onKeyDown = existing.onKeyDown
      this.onMotionChange = existing.onMotionChange
      this.tick = existing.tick
      instances.set(this.el, this)
      return
    }

    this.params = { ...BLUSH_PARAMS }
    this.phase = 0
    this.playing = !prefersReducedMotion()
    this.panelHidden = true
    this.panel = null
    this.renderer = null
    this.raf = 0
    this.lastTime = 0
    this.hint = null

    this.onKeyDown = this.onKeyDown.bind(this)
    this.onMotionChange = this.onMotionChange.bind(this)

    this.canvas = document.createElement('canvas')
    this.canvas.className = 'background__canvas'
    this.el.appendChild(this.canvas)

    try {
      this.renderer = createRenderer(this.canvas, vertSrc, fragSrc)
    } catch (error) {
      console.error('[Background]', error)
      return
    }

    this.el.classList.add('is-ready')
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.motionQuery.addEventListener('change', this.onMotionChange)
    window.addEventListener('keydown', this.onKeyDown)

    this.lastTime = performance.now()
    this.tick = this.tick.bind(this)
    this.raf = requestAnimationFrame(this.tick)

    instances.set(this.el, this)
  }

  tick(now) {
    const dt = (now - this.lastTime) / 1000
    this.lastTime = now

    if (this.panel) this.panel.fpsBegin()

    if (this.playing) {
      const dur = Math.max(0.001, this.params.loopDuration)
      this.phase = (this.phase + dt / dur) % 1
    }

    this.draw()

    if (this.panel) this.panel.fpsEnd()
    this.raf = requestAnimationFrame(this.tick)
  }

  draw(params = this.params, phase = this.phase) {
    if (!this.renderer || !this.canvas) return

    const cssW = this.el.clientWidth
    const cssH = this.el.clientHeight
    this.canvas.style.width = '100%'
    this.canvas.style.height = '100%'

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const bw = Math.max(1, Math.round(cssW * dpr))
    const bh = Math.max(1, Math.round(cssH * dpr))
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw
      this.canvas.height = bh
    }

    this.renderer.renderFrame({
      params: { ...params, pixelScale: dpr },
      phase,
    })

    this.syncPortals()
  }

  syncPortals() {
    if (!this.canvas || !this.canvas.width) return

    const portals = document.querySelectorAll('canvas[data-background-portal]')
    if (!portals.length) return

    const src = this.canvas
    const srcW = src.width
    const srcH = src.height
    const viewW = window.innerWidth || 1
    const viewH = window.innerHeight || 1

    portals.forEach((portal) => {
      const rect = portal.getBoundingClientRect()
      if (rect.width < 1 || rect.height < 1) return
      if (
        rect.bottom < 0 ||
        rect.top > viewH ||
        rect.right < 0 ||
        rect.left > viewW
      ) {
        return
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const pw = Math.max(1, Math.round(rect.width * dpr))
      const ph = Math.max(1, Math.round(rect.height * dpr))
      if (portal.width !== pw || portal.height !== ph) {
        portal.width = pw
        portal.height = ph
      }

      const sx = (rect.left / viewW) * srcW
      const sy = (rect.top / viewH) * srcH
      const sw = (rect.width / viewW) * srcW
      const sh = (rect.height / viewH) * srcH

      const ctx = portal.getContext('2d')
      if (!ctx) return
      ctx.clearRect(0, 0, pw, ph)
      ctx.drawImage(src, sx, sy, sw, sh, 0, 0, pw, ph)
    })
  }

  applyParams(partial) {
    this.params = { ...this.params, ...partial }
  }

  exportStill() {
    if (!this.renderer || !this.canvas) return

    const prevW = this.canvas.width
    const prevH = this.canvas.height
    const prevParams = this.params

    this.canvas.width = EXPORT_WIDTH
    this.canvas.height = EXPORT_HEIGHT
    this.renderer.renderFrame({
      params: { ...prevParams, pixelScale: 1 },
      phase: this.phase,
    })

    const a = document.createElement('a')
    a.download = `grain-gradient-${Date.now()}.png`
    a.href = this.canvas.toDataURL('image/png')
    a.click()

    this.canvas.width = prevW
    this.canvas.height = prevH
    this.draw(prevParams, this.phase)
  }

  showPanel() {
    if (this.panel) return

    this.panel = createPanel({
      params: this.params,
      playing: this.playing,
      builtinPresets: {
        blush: toSerializable(BLUSH_PARAMS),
      },
      onParamsChange: (partial) => this.applyParams(partial),
      onPlayingChange: (playing) => {
        this.playing = playing
      },
      onExportStill: () => this.exportStill(),
    })

    this.hint = document.createElement('div')
    this.hint.className = 'background__hint'
    this.hint.setAttribute('aria-hidden', 'true')
    this.hint.textContent = 'H hide panel'
    document.body.appendChild(this.hint)
  }

  hidePanel() {
    if (this.panel) {
      this.panel.dispose()
      this.panel = null
    }
    if (this.hint) {
      this.hint.remove()
      this.hint = null
    }
  }

  onKeyDown(event) {
    if (event.key !== 'h' && event.key !== 'H') return
    const target = event.target
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return
    }

    this.panelHidden = !this.panelHidden
    if (this.panelHidden) {
      this.hidePanel()
    } else {
      this.showPanel()
    }
  }

  onMotionChange() {
    if (this.motionQuery.matches) {
      this.playing = false
      if (this.panel) this.panel.refresh(this.params, this.playing)
    }
  }

  destroy() {
    // Keep the WebGL canvas alive across Highway page transitions.
    if (this.el?.hasAttribute('data-background-persist') && this.el.isConnected) {
      instances.set(this.el, this)
      return
    }

    cancelAnimationFrame(this.raf)
    window.removeEventListener('keydown', this.onKeyDown)
    if (this.motionQuery) {
      this.motionQuery.removeEventListener('change', this.onMotionChange)
    }
    this.hidePanel()
    if (this.renderer) {
      this.renderer.destroy()
      this.renderer = null
    }
    if (this.canvas) {
      this.canvas.remove()
      this.canvas = null
    }
    if (this.el) {
      instances.delete(this.el)
    }
  }
}
